import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { BRAND, JOURNEY, MODULES, PROBLEMS, PLANS, PRICING_DISCLAIMER } from "../lib/brand";
import { Atmosphere, CTA, DemoTag, Eyebrow, GlassCard, SectionHeading, Shell } from "../components/site/Primitives";
import HeroDiagram from "../components/site/HeroDiagram";
import ReceptionistDemo from "../components/site/ReceptionistDemo";

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden pb-20 pt-28 sm:pt-36">
        <Atmosphere />
        <Shell className="relative">
          <div className="grid items-center gap-14 lg:grid-cols-[1.05fr_1fr]">
            <div className="reveal">
              <Eyebrow testId="hero-eyebrow">AI Automation Agency</Eyebrow>
              <h1
                data-testid="hero-headline"
                className="mt-7 text-4xl font-semibold leading-[1.02] text-elv-text sm:text-5xl lg:text-6xl"
              >
                Your Business.
                <br />
                <span className="text-accent-gradient">Automated by AI.</span>
              </h1>
              <p data-testid="hero-sub" className="mt-7 max-w-xl text-base text-elv-dim md:text-lg">
                Elvoria AI builds intelligent business operating systems that capture leads, answer customers,
                book appointments, automate follow-ups, manage relationships, and give your team one place to
                run the business.
              </p>
              <div className="mt-9 flex flex-wrap items-center gap-3">
                <CTA to="/book-demo" testId="hero-cta-primary">
                  Book a Free AI Demo
                </CTA>
                <CTA to="/features" variant="ghost" testId="hero-cta-secondary">
                  Explore the Platform
                </CTA>
                <Link
                  to="/how-it-works"
                  data-testid="hero-cta-tertiary"
                  className="px-2 py-3 text-sm text-elv-dim underline decoration-elv-green/40 underline-offset-4 transition-colors duration-300 hover:text-elv-text"
                >
                  See How It Works
                </Link>
              </div>
              <p className="mt-8 max-w-lg text-sm text-elv-dim">{BRAND.support}</p>
            </div>
            <div className="reveal" style={{ animationDelay: "160ms" }}>
              <HeroDiagram />
            </div>
          </div>
        </Shell>
      </section>

      {/* JOURNEY */}
      <section className="relative border-y border-elv-line bg-[#070A0F] py-24">
        <Shell>
          <SectionHeading
            testId="journey-heading"
            eyebrow="End to end"
            title="One AI system. Your entire customer journey."
            sub="Elvoria AI connects the whole revenue workflow — from the first enquiry to long-term retention — so nothing depends on someone remembering to follow up."
          />
          <div className="mt-14 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {JOURNEY.map((step, i) => (
              <GlassCard key={step} testId={`journey-step-${i}`} className="p-5">
                <span className="font-mono text-[11px] text-elv-green">{String(i + 1).padStart(2, "0")}</span>
                <p className="mt-3 text-sm font-medium text-elv-text">{step}</p>
                <div className="mt-4 h-px w-full bg-gradient-to-r from-elv-green/40 to-transparent" />
              </GlassCard>
            ))}
          </div>
          <p className="mt-8 max-w-2xl text-sm text-elv-dim">
            Each stage writes back to one shared record, so your pipeline, calendar, inbox and reporting stay
            in agreement without manual data entry.
          </p>
        </Shell>
      </section>

      {/* PROBLEM */}
      <section className="relative py-24">
        <Shell>
          <SectionHeading
            testId="problem-heading"
            eyebrow="The real bottleneck"
            title="Most businesses don't have a lead problem. They have a follow-up problem."
            sub="Demand usually exists. What breaks is the operational work between the first conversation and the final conversion."
          />
          <div className="mt-14 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {PROBLEMS.map(([title, detail], i) => (
              <GlassCard key={title} testId={`problem-card-${i}`} className="p-5">
                <div className="flex items-start gap-3">
                  <Icons.AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-300/80" />
                  <div>
                    <p className="text-sm font-medium text-elv-text">{title}</p>
                    <p className="mt-2 text-xs leading-relaxed text-elv-dim">{detail}</p>
                  </div>
                </div>
              </GlassCard>
            ))}
          </div>
        </Shell>
      </section>

      {/* SOLUTION MODULES */}
      <section className="relative border-y border-elv-line bg-[#070A0F] py-24">
        <Shell>
          <SectionHeading
            testId="solution-heading"
            eyebrow="The platform"
            title="Meet the Elvoria AI Business Operating System."
            sub="Fifteen modules that behave as one system: shared data, shared history, one interface for your team."
          />
          <div className="mt-14 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {MODULES.map(([name, description, benefit, icon], i) => {
              const Icon = Icons[icon] || Icons.Sparkles;
              return (
                <GlassCard key={name} testId={`module-card-${i}`} className="group p-5">
                  <Icon className="h-5 w-5 text-elv-green" />
                  <p className="mt-4 text-sm font-semibold text-elv-text">{name}</p>
                  <p className="mt-2 text-xs leading-relaxed text-elv-dim">{description}</p>
                  <p className="mt-3 text-xs text-elv-cyan/90">{benefit}</p>
                  <Link
                    to="/features"
                    data-testid={`module-explore-${i}`}
                    className="mt-5 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-elv-dim transition-colors duration-300 group-hover:text-elv-green"
                  >
                    Explore <Icons.ArrowUpRight className="h-3 w-3" />
                  </Link>
                </GlassCard>
              );
            })}
          </div>
        </Shell>
      </section>

      {/* AI RECEPTIONIST */}
      <section className="relative py-24">
        <Shell>
          <div className="grid gap-14 lg:grid-cols-[0.9fr_1.1fr]">
            <div>
              <SectionHeading
                testId="receptionist-heading"
                eyebrow="AI Receptionist"
                title="Your AI receptionist never misses a call."
                sub="Turn missed calls into captured opportunities — with a defined scope, approved answers and a clean handover to humans."
              />
              <ul className="mt-8 grid gap-2.5">
                {[
                  "Answers inbound calls",
                  "Understands caller intent",
                  "Captures customer information",
                  "Qualifies leads",
                  "Answers approved business questions",
                  "Checks appointment availability",
                  "Books appointments",
                  "Confirms details",
                  "Transfers calls when required",
                  "Summarises calls",
                  "Updates the CRM",
                  "Can trigger follow-up workflows",
                ].map((item, i) => (
                  <li
                    key={item}
                    data-testid={`receptionist-capability-${i}`}
                    className="flex items-start gap-2.5 text-sm text-elv-dim"
                  >
                    <Icons.Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-elv-green" />
                    {item}
                  </li>
                ))}
              </ul>
              <p className="mt-7 text-xs leading-relaxed text-elv-dim">
                Capability is only active once the relevant voice, calendar and messaging providers are
                connected with your own credentials. We never present an unconnected integration as live.
              </p>
            </div>
            <div>
              <ReceptionistDemo />
            </div>
          </div>
        </Shell>
      </section>

      {/* CRM */}
      <section className="relative border-y border-elv-line bg-[#070A0F] py-24">
        <Shell>
          <SectionHeading
            testId="crm-heading"
            eyebrow="CRM"
            title="Every lead. Every customer. One source of truth."
            sub="A working CRM interface — pipeline stages, customer records, activity history and revenue — not a screenshot of one."
          />
          <div className="mt-12 grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
            {["New leads", "Contacted", "Qualified", "Booked", "Won", "Lost"].map((stage, i) => (
              <GlassCard key={stage} testId={`crm-stage-${i}`} className="p-4">
                <p className="text-[10px] uppercase tracking-[0.18em] text-elv-dim">{stage}</p>
                <p className="mt-3 font-display text-2xl text-elv-text">{[8, 6, 5, 4, 3, 2][i]}</p>
                <div className="mt-3 h-1 w-full rounded-full bg-white/5">
                  <div
                    className="h-1 rounded-full bg-elv-green/70"
                    style={{ width: `${[90, 70, 55, 42, 30, 20][i]}%` }}
                  />
                </div>
              </GlassCard>
            ))}
          </div>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <DemoTag testId="crm-demo-tag">Demonstration figures</DemoTag>
            <CTA to="/login" variant="ghost" testId="crm-open-portal">
              Open the client portal
            </CTA>
          </div>
          <div className="mt-10 grid gap-3 md:grid-cols-3">
            {[
              ["Customer record", "Name, phone, email, company, source, status, owner, tags and notes."],
              ["Full history", "Calls, messages, appointments, tasks, payments and automation runs."],
              ["Commercial view", "Lead score, revenue, payment status and next scheduled action."],
            ].map(([t, d], i) => (
              <GlassCard key={t} testId={`crm-detail-${i}`} className="p-5">
                <p className="text-sm font-semibold text-elv-text">{t}</p>
                <p className="mt-2 text-xs leading-relaxed text-elv-dim">{d}</p>
              </GlassCard>
            ))}
          </div>
        </Shell>
      </section>

      {/* AUTOMATION */}
      <section className="relative py-24">
        <Shell>
          <SectionHeading
            testId="automation-heading"
            eyebrow="Automation engine"
            title="Turn repetitive work into automatic workflows."
            sub="Define the trigger once. The system runs the sequence, records every execution and escalates to a human when it should."
          />
          <div className="mt-12 overflow-x-auto scrollbar-thin">
            <div className="flex min-w-max items-center gap-2">
              {[
                "New lead",
                "Create CRM record",
                "Qualify",
                "Follow up",
                "Book appointment",
                "Confirm",
                "Remind",
                "Complete",
                "Ask for review",
              ].map((step, i, arr) => (
                <div key={step} className="flex items-center gap-2">
                  <div
                    data-testid={`workflow-node-${i}`}
                    className="glass rounded-md px-3.5 py-2.5 text-[11px] uppercase tracking-[0.12em] text-elv-text"
                  >
                    {step}
                  </div>
                  {i < arr.length - 1 ? <Icons.ChevronRight className="h-3.5 w-3.5 text-elv-green/70" /> : null}
                </div>
              ))}
            </div>
          </div>
          <div className="mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {[
              "Missed Call Recovery",
              "New Lead Follow-Up",
              "Appointment Confirmation",
              "Appointment Reminder",
              "No-Show Recovery",
              "Unresponsive Lead Follow-Up",
              "Quote Follow-Up",
              "Review Request",
              "Re-Engagement",
              "Human Escalation",
              "Lead Assignment",
              "Payment Reminder",
            ].map((name, i) => (
              <GlassCard key={name} testId={`automation-card-${i}`} className="p-4">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-medium text-elv-text">{name}</p>
                  <Icons.Zap className="h-3.5 w-3.5 text-elv-cyan" />
                </div>
                <p className="mt-2 text-[11px] text-elv-dim">Trigger → actions → delay → logged execution</p>
              </GlassCard>
            ))}
          </div>
        </Shell>
      </section>

      {/* PRICING SNAPSHOT */}
      <section className="relative border-y border-elv-line bg-[#070A0F] py-24">
        <Shell>
          <SectionHeading
            testId="pricing-snapshot-heading"
            eyebrow="Pricing"
            title="Straightforward plans. No inflated promises."
            sub="Software and setup are fixed. Communication and AI model usage are stated separately, because those costs depend on your volume."
          />
          <div className="mt-14 grid gap-4 lg:grid-cols-3">
            {PLANS.map((plan, i) => (
              <GlassCard
                key={plan.key}
                testId={`home-plan-${plan.key}`}
                className={`p-6 ${plan.highlight ? "border-elv-green/30 bg-elv-green/[0.04]" : ""}`}
              >
                <div className="flex items-center justify-between">
                  <p className="text-[11px] uppercase tracking-[0.24em] text-elv-dim">{plan.name}</p>
                  {plan.highlight ? (
                    <span className="rounded-sm border border-elv-green/30 bg-elv-green/10 px-2 py-0.5 text-[10px] uppercase tracking-[0.16em] text-elv-green">
                      Most chosen
                    </span>
                  ) : null}
                </div>
                <p className="mt-5 font-display text-4xl text-elv-text">
                  ${plan.price}
                  <span className="text-sm text-elv-dim">/month</span>
                </p>
                <p className="mt-1 text-xs text-elv-dim">${plan.setup} setup</p>
                <p className="mt-4 text-xs leading-relaxed text-elv-dim">{plan.summary}</p>
                <ul className="mt-5 space-y-2">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-xs text-elv-dim">
                      <Icons.Check className="mt-0.5 h-3 w-3 shrink-0 text-elv-green" />
                      {f}
                    </li>
                  ))}
                </ul>
                <div className="mt-6">
                  <CTA to={plan.ctaLink || "/pricing"} variant={plan.highlight ? "primary" : "ghost"} testId={`home-plan-cta-${i}`}>
                    {plan.cta || "Compare plans"}
                  </CTA>
                </div>
              </GlassCard>
            ))}
          </div>
          <p className="mt-8 max-w-3xl text-xs leading-relaxed text-elv-dim">{PRICING_DISCLAIMER}</p>
        </Shell>
      </section>

      {/* FINAL CTA */}
      <section className="relative overflow-hidden py-28">
        <Atmosphere variant="page" />
        <Shell className="relative">
          <div className="glass rounded-xl p-8 sm:p-14">
            <h2
              data-testid="final-cta-heading"
              className="max-w-3xl text-3xl font-semibold leading-[1.06] text-elv-text sm:text-4xl lg:text-5xl"
            >
              Stop managing repetitive work.
              <br />
              <span className="text-accent-gradient">Start running an AI-powered business.</span>
            </h2>
            <p className="mt-6 max-w-2xl text-sm text-elv-dim md:text-base">
              Give your team one place to manage every customer interaction, and let AI handle the work between
              the first conversation and the final conversion.
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              <CTA to="/book-demo" testId="final-cta-book">
                Book Your Free AI Demo
              </CTA>
              <CTA to="/solutions" variant="ghost" testId="final-cta-explore">
                Explore Elvoria AI
              </CTA>
            </div>
          </div>
        </Shell>
      </section>
    </div>
  );
}
